import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

import PagedTable from "../shared/Tables/PagedTable";
import RegScanInput from "../shared/Inputs/RegScanInput";
import { ITriageQueueRow } from "../../models/Triage";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import TableVehicleDetails from "../vehicles/VehiclesTable/TableVehicleDetails";
import useTriage from "../../data/useTriage";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useNotification from "../notifications/useNotifications";
import { useScannerOverride } from "../../contexts/ScannerContext";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

const colHelper = createColumnHelper<ITriageQueueRow>();

export default function TriageTable() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { vrmLookupExisting } = useVehiclesMutations();
  const { createTriage } = useTriage();
  const isTme = CheckFeatureFlag("TME");
  // TME uses the Station Manager style reg input above the table; the same value
  // still filters the table via externalQuery.
  const [reg, setReg] = useState("");

  // Resolve a VRM to its triage (creating one if needed) and open it. The
  // triage page starts its timer on arrival once the vehicle has loaded.
  const handleLookupAndGo = async (query: string) => {
    const vrm = query?.replace(/\s/g, "").toUpperCase();
    if (!vrm) {
      addNotification({
        variant: "warn",
        primaryText: "Enter a VRM first",
      });
      return;
    }

    try {
      // Existing vehicles only — a typo'd reg must not conjure a new vehicle here.
      const vehicleResponse = await vrmLookupExisting.mutateAsync(vrm);
      const vehicle = vehicleResponse?.data;

      if (!vehicle?.id) {
        addNotification({
          variant: "warn",
          primaryText: `No vehicle found for ${vrm}`,
          secondaryText: "Check the reg, or add the vehicle first.",
        });
        return;
      }

      if (vehicle.triageId) {
        navigate(`/triage/${vehicle.triageId}`);
        return;
      }

      // TME buys before triaging, so don't create a triage for a vehicle that hasn't
      // been marked Purchased yet. An existing triage still opens (handled above).
      if (isTme && vehicle.financialDecision !== FinancialDecision.Purchased) {
        addNotification({
          variant: "warn",
          primaryText: "Not yet purchased",
        });
        return;
      }

      const triageResponse = await createTriage.mutateAsync({ vehicleId: vehicle.id });
      navigate(`/triage/${triageResponse.data.id}`);
    } catch (error) {
      addNotification({
        variant: "error",
        primaryText: "Unable to lookup VRM and start triage",
      });
    }
  };

  // Scanning a vehicle tag ("v|{vehicleId}|{vrm}") moves into its triage,
  // mirroring the Station Manager scan flow. We resolve via the scanned VRM,
  // reusing the same lookup as the "Go" button.
  useScannerOverride((scan) => {
    const { parts } = scan;
    if (parts[0]?.toLowerCase() !== "v") return;
    const vrm = parts[2];
    if (!vrm) {
      addNotification({ variant: "warn", primaryText: "Couldn't read the vehicle reg from that scan" });
      return;
    }
    handleLookupAndGo(vrm);
  });

  const columns = useMemo(
    () => [
      colHelper.accessor("vrm", {
        id: "Vehicle.Vrm",
        header: "VRM",
      }),
      colHelper.accessor("stockNumber", {
        id: "Vehicle.StockNumber",
        header: "Stock #",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "vehicleDetails",
        header: "Vehicle",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const vehicle = {
            id: row.vehicleId,
            vrm: row.vrm ?? "",
            stockNumber: row.stockNumber ?? "",
            combinedMake: row.combinedMake ?? "",
            combinedModel: row.combinedModel ?? "",
            modelRangeDescription: row.modelRangeDescription ?? "",
            engineModelCode: row.engineModelCode ?? "",
            combinedVIN: "",
            transmission: "",
            fuelType: "",
            vehicleImageUrl: row.vehicleImageUrl ?? "",
          } as IVehicle;

          return <TableVehicleDetails vehicle={vehicle} />;
        },
      }),
      colHelper.accessor("engineModelCode", {
        id: "Vehicle.EngineModelCode",
        header: "Engine Code",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "status",
        header: "Triage",
        enableSorting: false,
        cell: (info) => (
          <span>{info.row.original.completedSteps}/{info.row.original.totalSteps}</span>
        ),
      }),
    ],
    []
  );

  const isLookupLoading = vrmLookupExisting.isLoading || createTriage.isLoading;

  return (
    <div className="flex flex-col gap-4">
      {isTme && (
        <div className="rounded-lg bg-white p-4 shadow">
          <RegScanInput
            value={reg}
            onChange={setReg}
            onSubmit={() => handleLookupAndGo(reg)}
            buttonLabel="Go"
            isLoading={isLookupLoading}
          />
        </div>
      )}

      <PagedTable<ITriageQueueRow>
        key="triage-queue"
        url="/triage/filtered"
        columns={columns}
        queryLabel={isTme ? undefined : "Search VRM, stock, make, model"}
        externalQuery={isTme ? reg : undefined}
        queryButton={
          isTme
            ? undefined
            : {
                label: "Go",
                onClick: handleLookupAndGo,
                isLoading: isLookupLoading,
              }
        }
        showTotal={true}
        onRowClick={(row) => navigate(`/triage/${row.id}`)}
      />
    </div>
  );
}
